import React, { useRef, useState } from 'react';
import { useAuth } from "react-oidc-context";
import styles from "./POIList.module.css";
import { ActionButton, IIconProps, StackItem, Dropdown, MessageBar, MessageBarType, Dialog, DialogType, DialogFooter, PrimaryButton, DefaultButton, Spinner, SpinnerSize } from '@fluentui/react';
import { IStackTokens, Stack } from '@fluentui/react/lib/Stack';
import { POIItem } from "../../../model/ManagePOIListItem";
import { fetchPOI, downloadAllUploadedData, fetchDownloadMetadata, DownloadMetadata } from "../../../services/ManageSystemServices/POIService";
import AddEditDialog from "../Dialogs/AddEditPOIDialog";
import BulkUploadDialog from "../Dialogs/BulkUploadDialog";
import { IDropdownOption } from "@fluentui/react/lib/Dropdown";
import { NATURAL_PERSON_TYPE } from "../../../constants/EiopaFnPConstants";
import { displayMessageBar } from "../../LayoutWithAuth/LayoutWithAuth";
import { initializeIcons } from "@fluentui/font-icons-mdl2";
import { Tabs20Regular, Tabs20Filled, FluentIconsProps } from "@fluentui/react-icons";
import strings from "../../../loc/strings";

initializeIcons();

const stackTokens: IStackTokens = { childrenGap: 20 };

interface FilterPoiListProps {
    handleActionResponse: any;
    personTypeOptions: IDropdownOption[];
    selectedPersonType: number;
    isHistoricalPeriodEnded?: boolean;
}

function Filter(props: FilterPoiListProps) {

    const auth = useAuth();

    const [dialogIsHidden, setDialogIsHidden] = React.useState(true);
    const [dialogIsHiddenBulk, setDialogIsHiddenBulk] = React.useState(true);
    const [downloadDialogIsHidden, setDownloadDialogIsHidden] = React.useState(true);
    const [isDownloading, setIsDownloading] = React.useState(false);

    // Download metadata prefetch (work item 2781): fetched when the dialog OPENS so the
    // real server filename is known before the "Continue" gesture opens the file picker.
    // The ref is read synchronously in the Continue handler (no await → gesture preserved);
    // the state drives the count/spinner shown in the dialog.
    const downloadMetaRef = React.useRef<DownloadMetadata | null>(null);
    const [downloadRecordCount, setDownloadRecordCount] = React.useState<number | null>(null);
    const [isCountingRecords, setIsCountingRecords] = React.useState(false);
    const [countError, setCountError] = React.useState(false);

    const handleOpenDownloadDialog = () => {
        setDownloadDialogIsHidden(false);
        // Reset any state from a previous open, then prefetch (gesture 1). This resolves
        // while the user reads the confirmation text, so "Continue" already has the filename.
        downloadMetaRef.current = null;
        setDownloadRecordCount(null);
        setCountError(false);
        setIsCountingRecords(true);
        fetchDownloadMetadata(auth.user?.id_token)
            .then((meta) => {
                if (!Number.isFinite(meta.recordCount)) {
                    // We could not establish the exact record count — block the download
                    // rather than proceed with an unverifiable file.
                    setCountError(true);
                    return;
                }
                downloadMetaRef.current = meta;
                setDownloadRecordCount(meta.recordCount);
            })
            .catch(() => {
                // Prefetch failed — block the download and surface an error.
                setCountError(true);
            })
            .finally(() => setIsCountingRecords(false));
    };

    const [selectedPersonTypeInDialog, setSelectedPersonTypeInDialog] = useState(NATURAL_PERSON_TYPE);

    const handleOpenDialog = () => {
        setSelectedPersonTypeInDialog(NATURAL_PERSON_TYPE);
        setDialogIsHidden(false);
    };

    const handleOpenDialogBulk = () => {
        setDialogIsHiddenBulk(false);
    };

    const handleDownloadConfirm = async () => {
        setIsDownloading(true);
        try {
            // The modal stays open while the file streams; it shows the "Downloading…"
            // spinner (isDownloading) and is closed by the finally block when done.
            await downloadAllUploadedData(auth.user?.id_token, downloadMetaRef.current?.filename);
            displayMessageBar(MessageBarType.success, strings.downloadAllUploadedDataSuccess, false);
        } catch (e) {
            // Surface the specific failure message when the service provides one
            // (e.g. "download did not complete, file not saved"); otherwise fall
            // back to the generic error so the user is never left with a silent
            // failure and a possibly-truncated file.
            const message = e instanceof Error && e.message
                ? e.message
                : strings.downloadAllUploadedDataError;
            displayMessageBar(MessageBarType.error, message, false);
        } finally {
            setIsDownloading(false);
            setDownloadDialogIsHidden(true);
        }
    };

    const [items, setItems] = React.useState<POIItem[]>([]);

    // Icons
    const add: IIconProps = { iconName: 'Add' };
    const tab: IIconProps = { iconName: 'Tab' };

    const inputFile = useRef<HTMLInputElement | null>(null);
    const onClickAddFiles = () => {
        // current` points to the mounted file input element
        inputFile.current?.click();
    };
    const inputFileHist = useRef<HTMLInputElement | null>(null);
    const onClickAddFilesHist = () => {
        // current` points to the mounted file input element
        inputFileHist.current?.click();
    };

    const handleActionResponse = (response: string) => {
        props.handleActionResponse(response);
    }

    const handleActionResponseBulk = (response: string) => {
        handleActionResponse(response);
    }

    const iconStyleProps: FluentIconsProps = {
        primaryFill: "#0078D4",
        className: styles.filterIcon,
    };

    function onRenderPrefix() {
        return <Tabs20Regular aria-label={"Filter"} {...iconStyleProps} />;
    }

    return (
        <Stack>
            <StackItem className={styles.FilterBy}>
                <Stack horizontal horizontalAlign="end" tokens={stackTokens}>
                    <StackItem>
                        <ActionButton iconProps={add} onClick={handleOpenDialog} >
                            {strings.addPoiLabel}
                        </ActionButton>
                    </StackItem>
                    <StackItem>
                        <ActionButton onRenderIcon={onRenderPrefix} onClick={handleOpenDialogBulk}>
                            {strings.bulkUploadLabel}
                        </ActionButton>
                    </StackItem>
                    <StackItem>
                        <ActionButton iconProps={{ iconName: 'Download' }} onClick={handleOpenDownloadDialog} disabled={isDownloading}>
                            {strings.downloadAllUploadedDataLabel}
                        </ActionButton>
                    </StackItem>
                </Stack>
            </StackItem>
            <BulkUploadDialog
                dialogIsHiddenBulk={dialogIsHiddenBulk}
                setDialogIsHiddenBulk={setDialogIsHiddenBulk}
                title={strings.bulkUploadOfDataLabel}
                handleActionResponseBulk={handleActionResponseBulk}
                isHistoricalPeriodEnded={props.isHistoricalPeriodEnded}
            />
            <Dialog
                hidden={downloadDialogIsHidden}
                onDismiss={() => { if (!isDownloading) setDownloadDialogIsHidden(true); }}
                dialogContentProps={{
                    type: DialogType.normal,
                    title: strings.downloadAllUploadedDataDialogTitle,
                    // Hide the confirm prompt once the transfer is in progress — the modal
                    // then shows only the "Downloading…" spinner.
                    subText: isDownloading ? undefined : strings.downloadAllUploadedDataDialogMessage,
                }}
            >
                {isDownloading ? (
                    <Spinner
                        size={SpinnerSize.large}
                        label={strings.downloadAllUploadedDataDownloading}
                        labelPosition="bottom"
                        styles={{ root: { padding: '16px 0' } }}
                    />
                ) : (
                    <>
                        {isCountingRecords && (
                            <Spinner
                                size={SpinnerSize.small}
                                label={strings.downloadAllUploadedDataCounting}
                                labelPosition="right"
                                styles={{ root: { justifyContent: 'flex-start' } }}
                            />
                        )}
                        {!isCountingRecords && !countError && downloadRecordCount !== null && (
                            <p className={styles.downloadRecordCount}>
                                <strong>{downloadRecordCount.toLocaleString()}</strong>{' '}
                                {strings.downloadAllUploadedDataRecordsLabel}
                            </p>
                        )}
                        {!isCountingRecords && countError && (
                            <MessageBar messageBarType={MessageBarType.error} isMultiline>
                                {strings.downloadAllUploadedDataCountError}
                            </MessageBar>
                        )}
                    </>
                )}
                {!isDownloading && (
                    <DialogFooter>
                        <PrimaryButton
                            onClick={handleDownloadConfirm}
                            text={strings.downloadAllUploadedDataContinueButton}
                            disabled={isCountingRecords || countError}
                        />
                        <DefaultButton
                            onClick={() => setDownloadDialogIsHidden(true)}
                            text={strings.downloadAllUploadedDataCancelButton}
                        />
                    </DialogFooter>
                )}
            </Dialog>
            <AddEditDialog
                items={items}
                setItems={setItems}
                dialogIsHidden={dialogIsHidden}
                setDialogIsHidden={setDialogIsHidden}
                title={strings.addPoiDetailsLabel}
                handleActionResponse={handleActionResponse}
                isAdd={true}
                selectedPersonTypeInDialog={selectedPersonTypeInDialog}
                setSelectedPersonTypeInDialog={setSelectedPersonTypeInDialog}
                isHistoricalPeriodEnded={props.isHistoricalPeriodEnded}
            />
        </Stack>
    )
}

export default Filter;